(function () {
  const ROOT_SELECTOR = '[data-nawa-video-journal]';
  const states = new WeakMap();
  let activeVideo = null;
  let activeExternalMedia = null;

  function setMediaState(media, isPlaying) {
    if (!media) return;
    const root = media.closest(ROOT_SELECTOR);
    const toggle = media.querySelector('[data-nawa-journal-toggle]');
    media.classList.toggle('is-playing', isPlaying);
    if (toggle && root) {
      toggle.setAttribute('aria-label', isPlaying ? root.dataset.pauseLabel : root.dataset.playLabel);
    }
  }

  function stopExternal(media) {
    if (!media) return;
    const container = media.querySelector('[data-nawa-journal-external]');
    const poster = media.querySelector('[data-nawa-journal-poster]');
    if (container) {
      container.replaceChildren();
      container.hidden = true;
    }
    if (poster) poster.hidden = false;
    setMediaState(media, false);
    if (activeExternalMedia === media) activeExternalMedia = null;
  }

  function pauseOthers(exceptVideo, exceptMedia) {
    document.querySelectorAll(`${ROOT_SELECTOR} [data-nawa-journal-video]`).forEach((video) => {
      if (video !== exceptVideo && !video.paused) video.pause();
    });
    if (activeExternalMedia && activeExternalMedia !== exceptMedia) {
      stopExternal(activeExternalMedia);
    }
  }

  function playExternal(media) {
    const videoId = media.dataset.externalVideoId;
    const container = media.querySelector('[data-nawa-journal-external]');
    const poster = media.querySelector('[data-nawa-journal-poster]');
    if (!videoId || !container) return;

    if (media.classList.contains('is-playing')) {
      stopExternal(media);
      return;
    }

    pauseOthers(null, media);
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.tiktok.com/player/v1/${encodeURIComponent(videoId)}?autoplay=1&loop=0&controls=1&progress_bar=1&play_button=1&volume_control=1&fullscreen_button=1&music_info=0&description=0&rel=0`;
    iframe.title = media.closest('[data-nawa-journal-story]')?.querySelector('.nawa-video-journal__story-title')?.textContent?.trim() || 'Product video';
    iframe.loading = 'eager';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    container.replaceChildren(iframe);
    container.hidden = false;
    if (poster) poster.hidden = true;
    activeExternalMedia = media;
    setMediaState(media, true);
  }

  function toggleMedia(media) {
    const video = media.querySelector('[data-nawa-journal-video]');
    if (!video) {
      playExternal(media);
      return;
    }

    if (video.paused) {
      pauseOthers(video, media);
      video.play().catch(() => setMediaState(media, false));
    } else {
      video.pause();
    }
  }

  function updateTimeline(video) {
    const media = video.closest('[data-nawa-journal-media]');
    const progress = media?.querySelector('[data-nawa-journal-timeline]');
    if (!progress) return;
    const ratio = Number.isFinite(video.duration) && video.duration > 0 ? video.currentTime / video.duration : 0;
    progress.style.width = `${Math.max(0, Math.min(1, ratio)) * 100}%`;
  }

  function bindVideo(video) {
    if (video.dataset.nawaJournalBound === 'true') return;
    video.dataset.nawaJournalBound = 'true';
    const media = video.closest('[data-nawa-journal-media]');
    video.addEventListener('play', () => {
      pauseOthers(video, media);
      activeVideo = video;
      setMediaState(media, true);
    });
    video.addEventListener('pause', () => {
      if (activeVideo === video) activeVideo = null;
      setMediaState(media, false);
    });
    video.addEventListener('ended', () => {
      video.currentTime = 0;
      updateTimeline(video);
      setMediaState(media, false);
    });
    video.addEventListener('timeupdate', () => updateTimeline(video));
  }

  function setCurrentStory(root, story) {
    if (!story) return;
    const current = story.dataset.storyNumber || '01';
    const progress = root.querySelector('[data-nawa-journal-progress]');
    const currentNode = progress?.querySelector('b');
    if (currentNode) currentNode.textContent = current;
  }

  function bindRoot(root) {
    if (states.has(root)) return;

    const header = root.querySelector('[data-nawa-journal-header]');
    const storyObserver = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (visible) setCurrentStory(root, visible.target);
    }, { rootMargin: '-15% 0px -55% 0px', threshold: [0.15, 0.35, 0.6] });

    const mediaObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.45) return;
        const media = entry.target;
        const video = media.querySelector('[data-nawa-journal-video]');
        if (video && !video.paused) video.pause();
        if (media.classList.contains('is-playing') && media.dataset.externalVideoId) stopExternal(media);
      });
    }, { threshold: [0, 0.45, 0.8] });

    root.querySelectorAll('[data-nawa-journal-story]').forEach((story) => storyObserver.observe(story));
    root.querySelectorAll('[data-nawa-journal-media]').forEach((media) => mediaObserver.observe(media));
    root.querySelectorAll('[data-nawa-journal-video]').forEach(bindVideo);

    const onClick = (event) => {
      const toggle = event.target.closest('[data-nawa-journal-toggle]');
      if (toggle && root.contains(toggle)) {
        toggleMedia(toggle.closest('[data-nawa-journal-media]'));
        return;
      }

      const indexLink = event.target.closest('[data-nawa-journal-index-link]');
      if (!indexLink || !root.contains(indexLink)) return;
      const target = root.querySelector(indexLink.hash);
      if (!target) return;
      event.preventDefault();
      target.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start'
      });
    };

    const onScroll = () => {
      if (header) header.classList.toggle('is-condensed', window.scrollY > 110);
    };

    root.addEventListener('click', onClick);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    states.set(root, { storyObserver, mediaObserver, onClick, onScroll });
  }

  function unbindRoot(root) {
    const state = states.get(root);
    if (!state) return;
    state.storyObserver.disconnect();
    state.mediaObserver.disconnect();
    root.removeEventListener('click', state.onClick);
    window.removeEventListener('scroll', state.onScroll);
    states.delete(root);
  }

  function init(scope) {
    const roots = [];
    if (scope?.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope?.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
    roots.forEach(bindRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(ROOT_SELECTOR).forEach(unbindRoot);
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      if (activeVideo && !activeVideo.paused) activeVideo.pause();
      if (activeExternalMedia) stopExternal(activeExternalMedia);
    }
  });
})();
